import { useReactiveVar, type WatchQueryFetchPolicy } from "@apollo/client";
import { type AuthorizationParams, useAuth0 } from "@auth0/auth0-react";
import { captureException } from "@sentry/browser";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { user } from "@/store";

import useAnalytics from "./use-analytics";

const STORAGE_KEY = "auth_redirect_path";

export const clearStaleTokens = () => {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith("@@auth0spajs@@::")) {
      localStorage.removeItem(key);
    }
  }
};

const TokenContext = createContext({
  authToken: "",
  isLoading: true,
});

export function TokenProvider({ children }: { children: React.ReactNode }) {
  const [authToken, setAuthToken] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const auth0 = useAuth0();

  useEffect(() => {
    if (authToken !== "") {
      return;
    }
    if (auth0.isLoading) {
      return;
    }

    const storedTokens = Object.fromEntries(
      Object.entries(localStorage).filter(([key]) =>
        key.startsWith("@@auth0spajs@@::")
      )
    );

    const getToken = async () => {
      try {
        const token = await auth0.getAccessTokenSilently();
        setAuthToken(token);
        // biome-ignore lint/suspicious/noExplicitAny: legacy
      } catch (e: any) {
        if (e.error !== "timeout") {
          clearStaleTokens();
        }

        if (Object.keys(storedTokens).length > 0) {
          const isIssuedThisYear = Object.values(storedTokens).some((token) => {
            try {
              const parsedToken = JSON.parse(token);
              const iat = parsedToken.decodedToken?.claims?.iat;
              return iat && new Date(iat * 1000).getFullYear() >= 2025;
            } catch (_e) {
              /* Expected */
            }

            return false;
          }, true);

          if (isIssuedThisYear) {
            captureException(e, {
              extra: {
                browserInfo: {
                  language: navigator.language,
                  platform: navigator.platform,
                  userAgent: navigator.userAgent,
                },
                error: e.error,
                errorDescription: e.error_description,
                hadPreviousToken: !!authToken,
                lastTokenRefresh: new Date().toISOString(),
                storedTokens,
                timestamp: new Date().toISOString(),
              },
            });
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    getToken();
  }, [auth0, authToken]);

  return (
    <TokenContext.Provider value={{ authToken, isLoading }}>
      {children}
    </TokenContext.Provider>
  );
}

export default function useAuth() {
  const { authToken, isLoading } = useContext(TokenContext);

  const analytics = useAnalytics();
  const auth0 = useAuth0();
  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentUser = useReactiveVar(user);

  const login = (authorizationParams?: AuthorizationParams) => {
    // Save the current URL so we can redirect back to it after login
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ asPath: href, pathname })
    );
    auth0.loginWithRedirect({ authorizationParams });
  };

  const logout = () => {
    // Save the current URL so we can redirect back to it after logout
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ asPath: href, pathname })
    );
    analytics.reset();
    auth0.logout();
  };

  const signup = () => login({ screen_hint: "signup" });

  const isMe = useCallback(
    (otherUser: { id: string }) => currentUser?.id === otherUser.id,
    [currentUser]
  );

  const isUser = useMemo(() => authToken !== "", [authToken]);

  const mutationContext = useCallback(
    <T,>(variables?: T) => {
      if (authToken !== "") {
        return {
          context: {
            headers: { authorization: `JWT ${authToken}` },
          },
          variables,
        };
      }

      return { variables };
    },
    [authToken]
  );

  const queryContext = useCallback(
    <T,>(variables?: T) => {
      const options = mutationContext(variables);
      return authToken === ""
        ? options
        : {
            ...options,
            fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
          };
    },
    [authToken, mutationContext]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: its ok
  useEffect(() => {
    const routerData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");

    if (!routerData.asPath) {
      return;
    }

    // if the user came from the homepage, redirect them to the games page
    // otherwise, redirect them back to the page they were on
    const cameFromHome = routerData.pathname === "/";

    if (cameFromHome) {
      navigate({ replace: true, to: "/explore" });
    } else {
      navigate({ replace: true, to: routerData.asPath });
    }

    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    ...auth0,
    authToken,
    isLoading,
    isMe,
    isUser,
    login,
    logout,
    me: currentUser,
    mutationContext,
    queryContext,
    signup,
  };
}
